import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search } from "lucide-react";

const STAGES = ["Shortlisted","Interview","Technical Interview","Managerial Interview","HR Interview","Selected","On Hold","Rejected"];

export default function RecruiterApplications() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = React.useCallback(()=>{
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    api.get(`/recruiter/applications?${p.toString()}`).then(r=>setRows(r.data));
  },[q]);
  useEffect(()=>{ load(); },[load]);
  const updateStage = async (id, stage) => {
    try {
      await api.post(`/recruiter/applications/${id}/status`, { stage });
      toast.success(`Moved to ${stage}`);
      load();
    } catch(e){ toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F59E0B]">Recruiter</div>
          <h1 className="font-display text-3xl font-bold text-white mt-1">Applications</h1>
          <p className="text-slate-400 mt-1">{rows.length} record{rows.length!==1?"s":""} across your assigned jobs</p>
        </div>
      </div>
      <Card className="bg-slate-900 border-slate-800 p-4 mb-6">
        <div className="flex items-center gap-2 bg-slate-800 rounded-md px-3">
          <Search className="w-4 h-4 text-slate-400"/>
          <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search candidate, job, ID..." className="border-0 bg-transparent text-white focus-visible:ring-0" data-testid="rec-apps-search"/>
        </div>
      </Card>
      <Card className="bg-slate-900 border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950 text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Candidate</th>
                <th className="text-left px-4 py-3">Job</th>
                <th className="text-left px-4 py-3">Assessment</th>
                <th className="text-left px-4 py-3">Stage</th>
                <th className="text-left px-4 py-3">Update</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map(a=>(
                <tr key={a.id} className="hover:bg-slate-800/50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{a.candidate_name}</div>
                    <div className="text-xs text-slate-400">{a.candidate_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-white">{a.job_title}</div>
                    <div className="text-xs text-slate-400">{a.department} · {a.location}</div>
                  </td>
                  <td className="px-4 py-3">
                    {a.assessment_status === "completed" ? <Badge className="bg-emerald-500/20 text-emerald-400 border-none hover:bg-emerald-500/20">{a.assessment_score}%</Badge> :
                     <Badge className="bg-amber-500/20 text-amber-400 border-none hover:bg-amber-500/20">Pending</Badge>}
                  </td>
                  <td className="px-4 py-3"><Badge className="bg-slate-700 text-white border-none hover:bg-slate-700">{a.stage}</Badge></td>
                  <td className="px-4 py-3">
                    <Select onValueChange={(v)=>updateStage(a.id, v)}>
                      <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white h-8 text-xs" data-testid={`rec-stage-${a.id}`}><SelectValue placeholder="Move to..."/></SelectTrigger>
                      <SelectContent>{STAGES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-slate-500">No applications for your assigned jobs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminLayout>
  );
}
